import React, { useState } from 'react';
import { Alert, FlatList, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, useTheme } from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { useAuth } from '../context/AuthContext';
import ExpenseService from '../services/expenseService';
import { Expense } from '../types/Expense';
import { ErrorLogger } from '../utils/errorLogger';
import { formatAmount } from '../utils/formatAmount';
import { formatRelativeDate } from '../utils/formatDate';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import { useFirestoreSubscription } from '../hooks/useFirestoreSubscription';

interface ExpenseListProps {
  onDeleteSuccess?: () => void;
}

const ExpenseList: React.FC<ExpenseListProps> = ({ onDeleteSuccess }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { data: expenses, loading } = useFirestoreSubscription(
    (onUpdate, onError) => {
      if (!user?.uid) { onUpdate([]); return () => {}; }
      return ExpenseService.subscribeToAllExpenses(onUpdate, onError);
    },
    [] as Expense[],
  );

  const [expenseToDelete, setExpenseToDelete] = useState<Expense | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteConfirm = async () => {
    if (!expenseToDelete?.id) return;
    try {
      setDeleting(true);
      await ExpenseService.deleteExpense(expenseToDelete.id);
      onDeleteSuccess?.();
    } catch (error) {
      ErrorLogger.logError(error as any, 'ExpenseList.handleDeleteConfirm');
      Alert.alert('Error', 'Failed to delete expense. Please try again.');
    } finally {
      setDeleting(false);
      setExpenseToDelete(null);
    }
  };

  const renderItem = ({ item }: { item: Expense }) => (
    <View style={[styles.row, { backgroundColor: theme.colors.surface, borderBottomColor: theme.colors.outline }]}>
      <View style={[styles.rowAccent, { backgroundColor: theme.colors.error }]} />
      <View style={styles.rowContent}>
        <Text style={[styles.rowTitle, { color: theme.colors.onSurface }]} numberOfLines={1}>
          {item.description}
        </Text>
        <Text style={[styles.rowSubtitle, { color: theme.colors.onSurfaceVariant }]} numberOfLines={1}>
          {formatRelativeDate(item.createdAt)}
          {item.category ? ` · ${item.category}` : ''}
          {item.truck ? ` · ${item.truck.plateNumber}` : ''}
        </Text>
      </View>
      <Text style={[styles.rowAmount, { color: theme.colors.error }]}>
        -{formatAmount(item.amount)}
      </Text>
      <TouchableOpacity style={styles.deleteBtn} onPress={() => setExpenseToDelete(item)}>
        <MaterialCommunityIcons name="delete-outline" size={18} color={theme.colors.onSurfaceVariant} />
      </TouchableOpacity>
    </View>
  );

  if (loading) {
    return (
      <View style={styles.loading}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <FlatList
        data={expenses}
        renderItem={renderItem}
        keyExtractor={item => item.id || ''}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={
          <View style={styles.empty}>
            <MaterialCommunityIcons name="receipt-outline" size={48} color={theme.colors.onSurfaceVariant} />
            <Text style={[styles.emptyTitle, { color: theme.colors.onSurface }]}>No expenses yet</Text>
            <Text style={[styles.emptySubtitle, { color: theme.colors.onSurfaceVariant }]}>
              Tap + to record an expense
            </Text>
          </View>
        }
        contentContainerStyle={expenses.length === 0 ? styles.emptyContainer : undefined}
      />
      <ConfirmDeleteDialog
        visible={expenseToDelete !== null}
        onDismiss={() => setExpenseToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Expense"
        message="Are you sure you want to delete this expense?"
        itemSummary={expenseToDelete ? `${expenseToDelete.description} · ${formatAmount(expenseToDelete.amount)}` : undefined}
        loading={deleting}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  loading: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  rowAccent: {
    width: 3,
    alignSelf: 'stretch',
    borderRadius: 2,
    marginRight: 12,
  },
  rowContent: {
    flex: 1,
    paddingVertical: 14,
    paddingRight: 8,
  },
  rowTitle: {
    fontFamily: 'Inter-Medium',
    fontSize: 15,
    marginBottom: 3,
  },
  rowSubtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
  },
  rowAmount: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 15,
    marginRight: 8,
  },
  deleteBtn: {
    padding: 10,
    marginRight: 6,
  },
  empty: {
    alignItems: 'center',
    gap: 8,
  },
  emptyTitle: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 17,
  },
  emptySubtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
  },
  emptyContainer: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 48,
  },
});

export default ExpenseList;
